import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useTable } from 'react-table';
import './SystemSettingConfiguration.css';

import HeaderMenu from '../Header/HeaderMenu';
import checkPermission from '../Utils/checkPermission';

import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert
} from '@mui/material';

import axiosApi from '../Utils/axiosApi';


const SystemSettingConfiguration = () => {

  // =========================================================
  // STATE
  // =========================================================

  const [configurations, setConfigurations] = useState([]);

  const [companies, setCompanies] = useState([]);

  const [userRoles, setUserRoles] = useState([]);

  const [loading, setLoading] = useState(true);

  const [isDialogOpen, setDialogOpen] = useState(false);

  const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const [selectedConfiguration, setSelectedConfiguration] =
    useState(null);

  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  const [successMessage, setSuccessMessage] = useState('');

  const [errorMessage, setErrorMessage] = useState('');

  // Filters
  const [pendingCompanyFilter, setPendingCompanyFilter] =
    useState('');

  const [pendingRoleFilter, setPendingRoleFilter] =
    useState('');

  // =========================================================
  // FORM
  // =========================================================

  const [formData, setFormData] = useState({
    parameterName: '',
    parameterType: '',
    parameterValue: '',
    tenantId: null,
    roleCode: null
  });


  // =========================================================
  // FETCH COMPANIES
  // =========================================================

  useEffect(() => {

    axiosApi
      .post('/company/getCompany', {})
      .then((response) => {

        const companyArray = Array.isArray(response.data)
          ? response.data
          : response.data?.companies || [];

        setCompanies(companyArray);

      })
      .catch((error) => {

        console.error(
          'Error fetching companies:',
          error
        );

      });

  }, []);


  // =========================================================
  // FETCH ROLES
  // =========================================================

  useEffect(() => {

    axiosApi
      .post('/role/getUserRole', {})
      .then((response) => {

        const rolesArray = Array.isArray(response.data)
          ? response.data
          : response.data?.userRoles || [];

        setUserRoles(rolesArray);

      })
      .catch((error) => {

        console.error(
          'Error fetching user roles:',
          error
        );

      });

  }, []);


  // =========================================================
  // FETCH CONFIGURATIONS
  // =========================================================

  const fetchData = useCallback(
    async (requestBody = {}) => {

      try {

        setLoading(true);

        const response = await axiosApi.post(
          '/systemSetting/getSystemSettingConfigurations',
          requestBody
        );

        const data = Array.isArray(response.data)
          ? response.data
          : response.data?.data || [];

        setConfigurations(data);

      } catch (error) {

        console.error(
          'Error fetching system setting configurations:',
          error
        );

        setConfigurations([]);

      } finally {

        setLoading(false);

      }

    },
    []
  );


  useEffect(() => {

    fetchData();

  }, [fetchData]);


  // =========================================================
  // COMPANY NAME
  // =========================================================

  const getCompanyName = useCallback(
    (tenantId) => {

      if (
        tenantId === null ||
        tenantId === undefined ||
        tenantId === ''
      ) {
        return 'Global';
      }

      const company = companies.find(
        (item) =>
          Number(item.id) === Number(tenantId)
      );

      return company?.companyName || 'Unknown';

    },
    [companies]
  );


  // =========================================================
  // ROLE NAME
  // =========================================================

  const getRoleName = useCallback(
  (roleCode) => {

    if (
      roleCode === null ||
      roleCode === undefined ||
      roleCode === ''
    ) {
      return 'All Roles';
    }

    const role = userRoles.find(
      (item) => item.roleCode === roleCode
    );

    return role?.description || roleCode;

  },
  [userRoles]
);
  // =========================================================
  // RESET FILTERS
  // =========================================================

  const handleResetFilters = () => {

    setPendingCompanyFilter('');

    setPendingRoleFilter('');

    fetchData();

  };


  // =========================================================
  // SEARCH
  // =========================================================

  const handleSearch = async () => {

    try {

      setLoading(true);

      const requestBody = {};

      if (pendingCompanyFilter) {

        requestBody.tenantId =
          Number(pendingCompanyFilter);

      }

      if (pendingRoleFilter) {

        requestBody.roleCode =
          pendingRoleFilter;

      }

      const response = await axiosApi.post(
        '/systemSetting/getSystemSettingConfigurations',
        requestBody
      );

      const data = Array.isArray(response.data)
        ? response.data
        : response.data?.data || [];

      setConfigurations(data);

    } catch (error) {

      console.error(
        'Error fetching filtered configurations:',
        error
      );

      setConfigurations([]);

    } finally {

      setLoading(false);

    }

  };


  // =========================================================
  // ADD CONFIGURATION
  // =========================================================

  const handleAdd = () => {

    setSelectedConfiguration(null);

    setFormData({
      parameterName: '',
      parameterType: '',
      parameterValue: '',
      tenantId: null,
      roleCode: null
    });

    setSuccessMessage('');

    setErrorMessage('');

    setDialogOpen(true);

  };


  // =========================================================
  // EDIT CONFIGURATION
  // =========================================================

  const handleEdit = useCallback((configuration) => {

    setSelectedConfiguration(configuration);

    setFormData({
      parameterName:
        configuration.parameterName || '',

      parameterType:
        configuration.parameterType || '',

      parameterValue:
        configuration.parameterValue !== null &&
        configuration.parameterValue !== undefined
          ? String(configuration.parameterValue)
          : '',

      tenantId:
        configuration.tenantId !== null &&
        configuration.tenantId !== undefined
          ? configuration.tenantId
          : null,

      roleCode:
        configuration.roleCode !== null &&
        configuration.roleCode !== undefined
          ? configuration.roleCode
          : null
    });

    setSuccessMessage('');

    setErrorMessage('');

    setDialogOpen(true);

  }, []);


  // =========================================================
  // CLOSE ADD / EDIT DIALOG
  // =========================================================

  const handleCloseDialog = () => {

    setDialogOpen(false);

    setSelectedConfiguration(null);

    setSuccessMessage('');

    setErrorMessage('');

  };


  // =========================================================
  // FIELD CHANGE
  // =========================================================

  const handleFieldChange = (field, value) => {

    setFormData((previous) => ({
      ...previous,
      [field]: value
    }));

  };


  // =========================================================
  // SAVE / UPDATE
  // =========================================================

  const handleSubmit = async () => {

  // =====================================================
  // VALIDATION
  // =====================================================

  if (!formData.parameterName.trim()) {
    setErrorMessage('Please enter Parameter Name.');
    setSuccessMessage('');
    return;
  }

  if (!formData.parameterType) {
    setErrorMessage('Please select Parameter Type.');
    setSuccessMessage('');
    return;
  }

  if (
    formData.parameterValue === null ||
    formData.parameterValue === undefined ||
    formData.parameterValue === ''
  ) {
    setErrorMessage('Please enter Parameter Value.');
    setSuccessMessage('');
    return;
  }

  try {

    // =====================================================
    // CHECK UPDATE / CREATE
    // =====================================================

    const isUpdate =
      selectedConfiguration &&
      selectedConfiguration.id !== null &&
      selectedConfiguration.id !== undefined;


    // =====================================================
    // DTO
    // =====================================================

    const dto = {

      // IMPORTANT:
      // ID is required by backend during UPDATE
      ...(isUpdate && {
        id: selectedConfiguration.id
      }),

      parameterName:
        formData.parameterName.trim(),

      parameterType:
        formData.parameterType,

      parameterValue:
        formData.parameterValue,

      tenantId:
        formData.tenantId === '' ||
        formData.tenantId === null ||
        formData.tenantId === undefined
          ? null
          : Number(formData.tenantId),

      roleCode:
        formData.roleCode === '' ||
        formData.roleCode === null ||
        formData.roleCode === undefined
          ? null
          : formData.roleCode
    };


    // =====================================================
    // REQUEST
    // =====================================================

    const request = {
      systemSettingConfigurationDTO: dto
    };


    // Debug - remove later
    console.log(
      'System Setting Request:',
      JSON.stringify(request, null, 2)
    );


    // =====================================================
    // UPDATE
    // =====================================================

    if (isUpdate) {

      await axiosApi.post(
        '/systemSetting/updateSystemSettingConfiguration',
        request
      );

      setSuccessMessage(
        'System Setting Configuration updated successfully.'
      );

    }

    // =====================================================
    // CREATE
    // =====================================================

    else {

      await axiosApi.post(
        '/systemSetting/createSystemSettingConfiguration',
        request
      );

      setSuccessMessage(
        'System Setting Configuration created successfully.'
      );

    }


    setErrorMessage('');

    await fetchData();


    // =====================================================
    // CLOSE DIALOG
    // =====================================================

    setTimeout(() => {

      setDialogOpen(false);

      setSelectedConfiguration(null);

      setSuccessMessage('');

    }, 1000);


  } catch (error) {

    console.error(
      'Error saving system setting configuration:',
      error
    );

    setErrorMessage(
      error?.response?.data?.message ||
      'Failed to save System Setting Configuration.'
    );

    setSuccessMessage('');

  }

};

  // =========================================================
  // DELETE CLICK
  // =========================================================

  const handleDeleteClick = useCallback((id) => {

    setDeleteConfirmId(id);

    setDeleteDialogOpen(true);

    setSuccessMessage('');

    setErrorMessage('');

  }, []);


  // =========================================================
  // DELETE CONFIRM
  // =========================================================

  const confirmDelete = async () => {

    if (!deleteConfirmId) {
      return;
    }

    try {

      await axiosApi.post(
  '/systemSetting/deleteSystemSettingConfiguration',
  {
    systemSettingConfigurationIds: [deleteConfirmId]
  }
);


      setDeleteDialogOpen(false);

      setDeleteConfirmId(null);

      await fetchData();

      setSuccessMessage(
        'System Setting Configuration deleted successfully.'
      );

      setErrorMessage('');


    } catch (error) {

      console.error(
        'Error deleting configuration:',
        error
      );

      setErrorMessage(
        error?.response?.data?.message ||
        'Failed to delete System Setting Configuration.'
      );

      setSuccessMessage('');

    }

  };


  // =========================================================
  // TABLE COLUMNS
  // =========================================================

  const columns = useMemo(
    () => [

      {
        Header: '#',
        accessor: 'id'
      },

      {
        Header: 'Parameter Name',
        accessor: 'parameterName'
      },

      {
        Header: 'Parameter Type',
        accessor: 'parameterType'
      },

      {
        Header: 'Parameter Value',
        accessor: 'parameterValue'
      },

      {
        Header: 'Company',
        accessor: 'tenantId',

        Cell: ({ value }) => {

          return getCompanyName(value);

        }
      },

      {
        Header: 'Role',
        accessor: 'roleCode',

        Cell: ({ value }) => {

          return getRoleName(value);

        }
      },

      {
        Header: 'Actions',
        accessor: 'actions',

        Cell: ({ row }) => (

          <div className="actions-container">

            <Button
              disabled={
                !checkPermission(
                  'Admin',
                  'Configuration',
                  'UPDATE'
                )
              }
              size="small"
              color="primary"
              variant="outlined"
              onClick={() =>
                handleEdit(row.original)
              }
            >
              Edit
            </Button>


            <Button
              disabled={
                !checkPermission(
                  'Admin',
                  'Configuration',
                  'DELETE'
                )
              }
              size="small"
              color="error"
              variant="outlined"
              onClick={() =>
                handleDeleteClick(
                  row.original.id
                )
              }
            >
              Delete
            </Button>

          </div>

        )
      }

    ],
    [
      getCompanyName,
      getRoleName,
      handleEdit,
      handleDeleteClick
    ]
  );


  // =========================================================
  // TABLE
  // =========================================================

  const tableInstance = useTable({
    columns,
    data: configurations
  });


  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow
  } = tableInstance;


  // =========================================================
  // RENDER
  // =========================================================

  return (

    <div>

      {/* ================================================= */}
      {/* COMMON HEADER */}
      {/* ================================================= */}

      <HeaderMenu />


      <div className="system-setting-configuration-container">


        {/* ================================================= */}
        {/* PAGE HEADER */}
        {/* ================================================= */}

        <Box
  className="page-header"
  sx={{
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 2,
    mb: 3,
    flexWrap: 'wrap',
  }}
>
  <Box>
    <h2 style={{ margin: 0 }}>
      System Setting Configuration
    </h2>

    <p style={{ margin: '6px 0 0' }}>
      Manage global, company and role-specific system settings.
    </p>
  </Box>

  <Button
    disabled={
      !checkPermission(
        'Admin',
        'Configuration',
        'CREATE'
      )
    }
    variant="contained"
    onClick={handleAdd}
    sx={{
      whiteSpace: 'nowrap',
      flexShrink: 0,
    }}
  >
    + Add Configuration
  </Button>
</Box>

        {/* ================================================= */}
        {/* SUCCESS / ERROR MESSAGE */}
        {/* ================================================= */}

        {successMessage && (

          <Alert
            severity="success"
            sx={{ mb: 2 }}
          >
            {successMessage}
          </Alert>

        )}


        {errorMessage && (

          <Alert
            severity="error"
            sx={{ mb: 2 }}
          >
            {errorMessage}
          </Alert>

        )}


        {/* ================================================= */}
        {/* FILTER BAR */}
        {/* ================================================= */}

        <Box
  sx={{
    display: 'flex',
    alignItems: 'stretch',
    gap: 2,
    backgroundColor: '#f9f9f9',
    p: 2,
    borderRadius: 2,
    mb: 3,
    boxShadow: 1,

    // Desktop
    flexWrap: 'wrap',

    // Mobile
    '@media (max-width: 600px)': {
      flexDirection: 'column',
      gap: 1.5,
    },
  }}
>
  {/* COMPANY */}
  <FormControl
    size="small"
    sx={{
      minWidth: 220,

      '@media (max-width: 600px)': {
        width: '100%',
        minWidth: 0,
      },
    }}
  >
    <InputLabel>Company</InputLabel>

    <Select
      value={pendingCompanyFilter}
      onChange={(e) =>
        setPendingCompanyFilter(e.target.value)
      }
      label="Company"
    >
      <MenuItem value="">
        All Companies
      </MenuItem>

      {companies.map((company) => (
        <MenuItem
          key={company.id}
          value={company.id}
        >
          {company.companyName}
        </MenuItem>
      ))}
    </Select>
  </FormControl>


  {/* ROLE */}
  <FormControl
    size="small"
    sx={{
      minWidth: 220,

      '@media (max-width: 600px)': {
        width: '100%',
        minWidth: 0,
      },
    }}
  >
    <InputLabel>Role</InputLabel>

    <Select
      value={pendingRoleFilter}
      onChange={(e) =>
        setPendingRoleFilter(e.target.value)
      }
      label="Role"
    >
      <MenuItem value="">
        All Roles
      </MenuItem>

      {userRoles.map((role) => (
        <MenuItem
          key={role.id}
          value={role.roleCode}
        >
          {role.description}
        </MenuItem>
      ))}
    </Select>
  </FormControl>


  {/* BUTTONS */}
  <Box
    sx={{
      display: 'flex',
      gap: 1,

      '@media (max-width: 600px)': {
        width: '100%',
      },
    }}
  >
    <Button
      variant="contained"
      size="small"
      onClick={handleSearch}
      sx={{
        '@media (max-width: 600px)': {
          flex: 1,
        },
      }}
    >
      Search
    </Button>

    <Button
      variant="outlined"
      size="small"
      color="secondary"
      onClick={handleResetFilters}
      sx={{
        '@media (max-width: 600px)': {
          flex: 1,
        },
      }}
    >
      Reset
    </Button>
  </Box>
</Box>


        {/* ================================================= */}
        {/* TABLE */}
        {/* ================================================= */}

        {loading ? (

          <div className="loading-container">

            <CircularProgress />

            <p>
              Loading system settings...
            </p>

          </div>

        ) : (

          <div className="table-container">

            <table
              {...getTableProps()}
              className="table"
            >

              <thead>

                {headerGroups.map(
                  (headerGroup) => (

                    <tr
                      {...headerGroup.getHeaderGroupProps()}
                      key={headerGroup.id}
                    >

                      {headerGroup.headers.map(
                        (column) => (

                          <th
                            {...column.getHeaderProps()}
                            key={column.id}
                          >
                            {column.render(
                              'Header'
                            )}
                          </th>

                        )
                      )}

                    </tr>

                  )
                )}

              </thead>


              <tbody
                {...getTableBodyProps()}
              >

                {rows.length === 0 ? (

                  <tr>

                    <td
                      colSpan={7}
                      style={{
                        textAlign: 'center',
                        padding: '30px'
                      }}
                    >
                      No system setting
                      configurations found.
                    </td>

                  </tr>

                ) : (

                  rows.map((row) => {

                    prepareRow(row);

                    return (

                      <tr
                        {...row.getRowProps()}
                        key={row.id}
                      >

                        {row.cells.map(
                          (cell) => (

                            <td
                              {...cell.getCellProps()}
                              key={cell.column.id}
                              data-label={
                                cell.column.Header
                              }
                            >
                              {cell.render('Cell')}
                            </td>

                          )
                        )}

                      </tr>

                    );

                  })

                )}

              </tbody>

            </table>

          </div>

        )}


        {/* ================================================= */}
        {/* CREATE / EDIT DIALOG */}
        {/* ================================================= */}

        {isDialogOpen && (

          <Dialog
            open={isDialogOpen}
            onClose={(event, reason) => {

              if (
                reason === 'backdropClick' ||
                reason === 'escapeKeyDown'
              ) {
                return;
              }

              handleCloseDialog();

            }}
            maxWidth="sm"
            fullWidth
          >

            <DialogTitle>

              {selectedConfiguration
                ? 'Edit System Setting Configuration'
                : 'Create System Setting Configuration'}

            </DialogTitle>


            <DialogContent>

              {/* SUCCESS */}

              {successMessage && (

                <Alert
                  severity="success"
                  sx={{ mb: 2 }}
                >
                  {successMessage}
                </Alert>

              )}


              {/* ERROR */}

              {errorMessage && (

                <Alert
                  severity="error"
                  sx={{ mb: 2 }}
                >
                  {errorMessage}
                </Alert>

              )}


              <div className="dialog-grid">


                {/* PARAMETER NAME */}

                <div>

                  <label>
                    Parameter Name:
                  </label>

                  <input
                    type="text"
                    value={
                      formData.parameterName
                    }
                    onChange={(e) =>
                      handleFieldChange(
                        'parameterName',
                        e.target.value
                      )
                    }
                    placeholder="Example: MAX_LOGIN_ATTEMPTS"
                  />

                </div>


                {/* PARAMETER TYPE */}

                <div>

                  <label>
                    Parameter Type:
                  </label>

                  <select
                    value={
                      formData.parameterType
                    }
                    onChange={(e) =>
                      handleFieldChange(
                        'parameterType',
                        e.target.value
                      )
                    }
                  >

                    <option value="">
                      Select Type
                    </option>

                    <option value="STRING">
                      String
                    </option>

                    <option value="INTEGER">
                      Integer
                    </option>

                    <option value="LONG">
                      Long
                    </option>

                    <option value="DOUBLE">
                      Decimal
                    </option>

                    <option value="BOOLEAN">
                      Boolean
                    </option>

                    <option value="DATE">
                      Date
                    </option>

                  </select>

                </div>


                {/* PARAMETER VALUE */}

                <div>

                  <label>
                    Parameter Value:
                  </label>


                  {formData.parameterType ===
                  'BOOLEAN' ? (

                    <select
                      value={
                        formData.parameterValue
                      }
                      onChange={(e) =>
                        handleFieldChange(
                          'parameterValue',
                          e.target.value
                        )
                      }
                    >

                      <option value="">
                        Select Value
                      </option>

                      <option value="true">
                        True
                      </option>

                      <option value="false">
                        False
                      </option>

                    </select>

                  ) : (

                    <input
                      type={
                        formData.parameterType ===
                          'INTEGER' ||
                        formData.parameterType ===
                          'LONG' ||
                        formData.parameterType ===
                          'DOUBLE'
                          ? 'number'
                          : 'text'
                      }
                      value={
                        formData.parameterValue
                      }
                      onChange={(e) =>
                        handleFieldChange(
                          'parameterValue',
                          e.target.value
                        )
                      }
                      placeholder="Enter parameter value"
                    />

                  )}

                </div>


                {/* COMPANY */}

                <div>

                  <label>
                    Company:
                  </label>

                  <select
                    value={
                      formData.tenantId ?? ''
                    }
                    onChange={(e) =>
                      handleFieldChange(
                        'tenantId',
                        e.target.value === ''
                          ? null
                          : e.target.value
                      )
                    }
                  >

                    <option value="">
                      Global / All Companies
                    </option>

                    {companies.map(
                      (company) => (

                        <option
                          key={company.id}
                          value={company.id}
                        >
                          {company.companyName}
                        </option>

                      )
                    )}

                  </select>

                </div>


                {/* ROLE */}

                <div>

  <label>
    Role:
  </label>

  <select
    value={formData.roleCode ?? ''}
    onChange={(e) =>
      handleFieldChange(
        'roleCode',
        e.target.value === ''
          ? null
          : e.target.value
      )
    }
  >

    <option value="">
      All Roles
    </option>

    {userRoles.map((role) => (

      <option
        key={role.id}
        value={role.roleCode}
      >
        {role.description}
      </option>

    ))}

  </select>

</div>


                {/* SCOPE INFORMATION */}

                <div
                  style={{
                    background: '#f5f5f5',
                    padding: '10px',
                    borderRadius: '5px'
                  }}
                >

                  <small>

                    <strong>
                      Scope:
                    </strong>

                    {' '}

                    {formData.tenantId
                      ? getCompanyName(
                          formData.tenantId
                        )
                      : 'Global'}

                    {' / '}

                    {formData.roleCode
                      ? getRoleName(
                          formData.roleCode
                        )
                      : 'All Roles'}

                  </small>

                </div>

              </div>

            </DialogContent>


            <DialogActions>

              <Button
                variant="outlined"
                onClick={
                  handleCloseDialog
                }
              >
                Close
              </Button>


              <Button
                disabled={
                  !checkPermission(
                    'Admin',
                    'Configuration',
                    selectedConfiguration
                      ? 'UPDATE'
                      : 'CREATE'
                  )
                }
                variant="contained"
                onClick={handleSubmit}
              >

                {selectedConfiguration
                  ? 'Update'
                  : 'Save'}

              </Button>

            </DialogActions>

          </Dialog>

        )}


        {/* ================================================= */}
        {/* DELETE CONFIRMATION */}
        {/* ================================================= */}

        {isDeleteDialogOpen && (

          <Dialog
            open={isDeleteDialogOpen}
            onClose={() =>
              setDeleteDialogOpen(false)
            }
            maxWidth="sm"
            fullWidth
          >

            <DialogTitle>
              Confirm Delete
            </DialogTitle>


            <DialogContent>

              <p>
                Are you sure you want to delete
                this System Setting Configuration?
              </p>

            </DialogContent>


            <DialogActions>

              <Button
                variant="contained"
                color="error"
                onClick={confirmDelete}
              >
                Yes, Delete
              </Button>


              <Button
                variant="outlined"
                onClick={() =>
                  setDeleteDialogOpen(false)
                }
              >
                Cancel
              </Button>

            </DialogActions>

          </Dialog>

        )}

      </div>

    </div>

  );

};


export default SystemSettingConfiguration;